import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Close } from '@mui/icons-material';
import { Box, Modal, Stack, Typography } from '@mui/material';

import CustomButton from 'components/Button';
import { Header } from 'components/GlobalStyles/styles';
import LabelledSelect from 'components/LabelledSelect';
import LabelledTextField from 'components/LabelledTextField';
import { MessageType } from 'components/SnackBar';
import { useAddTaxCodeMutation, useUpdateTaxCodeByIdMutation } from 'services/busmanApi';
import { TaxCodeResponse } from 'types/TaxcodeTypes';
import { DESCRIPTION_LIMIT, MYOB_TAX_TYPE } from 'utils/constants';
import { AddTaxCodeSchema, TaxCodeFormValues } from 'utils/schema';

interface TaxCodeModalProps {
  open: boolean;
  onClose: () => void;
  setSnackBarMessage: (message: MessageType) => void;
  taxCode?: TaxCodeResponse | null;
  onCreated?: (taxCode: TaxCodeResponse) => void;
}

const taxTypeOptions = Object.values(MYOB_TAX_TYPE).map(({ value, name }) => ({ value, label: name }));

const defaultTaxCodeValue: TaxCodeFormValues = {
  code: '',
  description: '',
  type: '',
  rate: '',
};

const TaxCodeModal: React.FC<TaxCodeModalProps> = ({ open, onClose, setSnackBarMessage, taxCode, onCreated }) => {
  const [addTaxCode, { isLoading: isAdding }] = useAddTaxCodeMutation();
  const [updateTaxCode, { isLoading: isUpdating }] = useUpdateTaxCodeByIdMutation();

  const isEditing = !!taxCode;
  const isLoading = isAdding || isUpdating;

  const { handleSubmit, control, reset } = useForm<TaxCodeFormValues>({
    defaultValues: defaultTaxCodeValue,
    resolver: zodResolver(AddTaxCodeSchema),
  });

  React.useEffect(() => {
    if (open)
      reset(
        taxCode
          ? {
              code: taxCode.code ?? '',
              description: taxCode.description ?? '',
              type: taxCode.type ?? '',
              rate: taxCode.rate ?? '',
            }
          : defaultTaxCodeValue,
      );
  }, [open, taxCode, reset]);

  const handleClose = () => {
    reset(defaultTaxCodeValue);
    onClose();
  };

  const onSubmit = async (data: TaxCodeFormValues) => {
    if (isEditing) {
      const result = await updateTaxCode({ ...data, tax_id: taxCode.tax_id });

      if ('error' in result) {
        setSnackBarMessage({ type: 'error', msg: `Error updating tax code due to, ${result.error}` });
        return;
      }

      setSnackBarMessage({ type: 'success', msg: 'Tax code updated successfully.' });
      handleClose();
      return;
    }

    const result = await addTaxCode(data);

    if ('error' in result) {
      setSnackBarMessage({ type: 'error', msg: `Error adding tax code due to, ${result.error}` });
      return;
    }

    const created = (result.data as { data?: TaxCodeResponse } | undefined)?.data;
    if (created) onCreated?.(created);

    setSnackBarMessage({ type: 'success', msg: 'Tax code added successfully.' });
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="taxcode-modal-title"
      aria-describedby="taxcode-modal-description">
      <Box
        sx={{
          p: 3,
          top: '50%',
          left: '50%',
          width: 450,
          maxWidth: '100vw',
          position: 'absolute',
          bgcolor: 'background.paper',
          borderRadius: 2,
          transform: 'translate(-50%, -50%)',
          boxShadow:
            '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
        }}>
        <Header>
          <Typography variant="h4">{isEditing ? 'Edit Tax Code' : 'New Tax Code'}</Typography>
          <Box onClick={handleClose} sx={{ cursor: 'pointer' }}>
            <Close sx={{ color: 'text.disabled', fontSize: '18px' }} />
          </Box>
        </Header>

        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
              <LabelledTextField dataTestId="taxcode-code-input" name="code" label="CODE" control={control} />
              <LabelledTextField dataTestId="taxcode-rate-input" name="rate" label="RATE (%)" control={control} />
            </Stack>

            <Stack direction="row">
              <LabelledSelect
                name="type"
                label="TYPE"
                control={control}
                options={taxTypeOptions}
                placeholder="Select tax type"
                selectSize="small"
              />
            </Stack>

            <Stack direction="row">
              <LabelledTextField
                dataTestId="taxcode-description-input"
                name="description"
                label="DESCRIPTION"
                multiline
                rules={{ maxLength: DESCRIPTION_LIMIT }}
                control={control}
              />
            </Stack>

            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ width: '100%' }}>
              <CustomButton
                testid="taxcode-cancel-button"
                variant="contained_grey"
                width="medium"
                text="Cancel"
                onClick={handleClose}
              />
              <CustomButton
                testid="taxcode-submit-button"
                type="submit"
                variant="contained"
                width="medium"
                text="Save"
                disabled={isLoading}
              />
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};

export default TaxCodeModal;
